import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { apiFetch } from '../../lib/api';
import { ExpiryBadge } from './expiry-badge';
import { formatQty } from './format-qty';
import type { SearchProductCard } from './types';
import { SkeletonRows } from '../skeleton';

/**
 * 全局搜索页（S7 / §27）：一个产品一张卡，卡内展开批次与当前位置分布。
 * 跨仓库（服务端按 user_warehouse_access 裁剪）；批次级显示 在手/预留/可用
 * 三个精确数字，位置行只显示物理在手（R-18）。
 */
export function SearchPage(): React.JSX.Element {
  const { t } = useTranslation();
  const [input, setInput] = useState('');
  const [query, setQuery] = useState('');

  const { data, isFetching } = useQuery({
    queryKey: ['global-search', query],
    queryFn: () =>
      apiFetch<{ items: SearchProductCard[] }>(`/search?q=${encodeURIComponent(query)}`),
    enabled: query.trim().length > 0,
  });

  return (
    <div>
      <h1 style={{ fontSize: 'var(--text-2xl)', marginBottom: 'var(--space-3)' }}>
        {t('search.title')}
      </h1>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          setQuery(input);
        }}
        className="row"
        style={{ gap: 'var(--space-2)', marginBottom: 'var(--space-4)' }}
      >
        <input
          className="field"
          style={{ flex: 1 }}
          autoFocus
          placeholder={t('search.placeholder')}
          value={input}
          onChange={(e) => setInput(e.target.value)}
        />
        <button type="submit" className="btn btn--primary">
          {t('app.search')}
        </button>
      </form>

      {isFetching && <SkeletonRows rows={4} rowHeight={64} bars={4} />}
      {!isFetching && query && (data?.items.length ?? 0) === 0 && (
        <div className="empty-state">
          <p>{t('search.noResults', { query })}</p>
        </div>
      )}

      {(data?.items ?? []).map((card) => (
        <div
          key={card.productId}
          className="card"
          style={{ padding: 'var(--space-4)', marginBottom: 'var(--space-3)' }}
        >
          <div className="row row--between">
            <div>
              <Link
                to={`/products/${card.productId}`}
                className="link"
                style={{ fontWeight: 600, fontSize: 'var(--text-lg)' }}
              >
                {card.name}
              </Link>
              <span className="field-hint" style={{ marginLeft: 'var(--space-2)' }}>
                SKU {card.sku}
              </span>
            </div>
            <div className="row" style={{ gap: 'var(--space-3)' }}>
              <span>
                {t('stock.onHand')}{' '}
                <strong>{formatQty(card.onHandBase, card.units, card.baseUnitCode)}</strong>
              </span>
              <span>
                {t('stock.reserved')}{' '}
                <strong>{formatQty(card.reservedBase, card.units, card.baseUnitCode)}</strong>
              </span>
              <span>
                {t('stock.available')}{' '}
                <strong>{formatQty(card.availableBase, card.units, card.baseUnitCode)}</strong>
              </span>
            </div>
          </div>

          {card.batches.length === 0 && (
            <p className="field-hint" style={{ marginTop: 'var(--space-2)' }}>
              {t('search.noStock')}
            </p>
          )}
          {card.batches.map((batch) => (
            <div
              key={batch.batchId}
              style={{
                marginTop: 'var(--space-3)',
                paddingLeft: 'var(--space-3)',
                borderLeft: '2px solid var(--color-border)',
              }}
            >
              <div className="row" style={{ gap: 'var(--space-2)', alignItems: 'center' }}>
                <strong>{batch.batchNo}</strong>
                {batch.externalLotNo && (
                  <span className="field-hint">
                    {t('search.externalLot', { lot: batch.externalLotNo })}
                  </span>
                )}
                <span>{formatQty(batch.onHandBase, card.units, card.baseUnitCode)}</span>
                <ExpiryBadge
                  expirationDate={batch.expirationDate}
                  hasExpiration={batch.hasExpiration}
                />
                {BigInt(batch.reservedBase) > 0n && (
                  <span className="badge badge--info">
                    {t('status.hasReservation')} {batch.reservedBase}
                  </span>
                )}
              </div>
              {batch.locations.map((loc) => (
                <div
                  key={loc.locationId}
                  className="row"
                  style={{
                    gap: 'var(--space-2)',
                    paddingLeft: 'var(--space-3)',
                    color: 'var(--color-text-muted)',
                  }}
                >
                  <span style={{ flex: 3 }}>
                    {loc.warehouseName} › {loc.pathLabel}
                  </span>
                  <span style={{ width: '140px', textAlign: 'right' }}>
                    {formatQty(loc.onHandBase, card.units, card.baseUnitCode)}
                  </span>
                </div>
              ))}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}
